import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  FaBullhorn, 
  FaSearch, 
  FaFilter, 
  FaBriefcase, 
  FaCalendarAlt, 
  FaBuilding, 
  FaMapMarkerAlt, 
  FaBookmark, 
  FaRegBookmark, 
  FaEye, 
  FaExternalLinkAlt,
  FaInfoCircle,
  FaClock,
  FaTag,
  FaSort,
  FaChevronDown,
  FaSyncAlt,
  FaExclamationTriangle,
  FaGraduationCap,
  FaDollarSign,
  FaHeart,
  FaRegHeart,
  FaBell,
  FaCheck,
  FaGlobe,
  FaTimes
} from 'react-icons/fa';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc, 
  getDoc 
} from 'firebase/firestore';
import { arrayUnion, arrayRemove } from 'firebase/firestore';
import { auth, db } from '../../configs/firebase';
import { AuthData } from '../../provider/AuthProvider';
import { Link } from 'react-router-dom';

const Announcements = () => {
  const { user } = AuthData();
  
  // State declarations
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [savedAnnouncements, setSavedAnnouncements] = useState([]);
  const [readAnnouncements, setReadAnnouncements] = useState([]);
  const [userInterests, setUserInterests] = useState([]);
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const [showTargetedOnly, setShowTargetedOnly] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [userProfile, setUserProfile] = useState({
    skills: [],
    interests: [],
    graduationYear: '',
    country: '',
    location: '',
    gpa: ''
  });

  const categories = [
    { id: 'all', label: 'All Announcements', icon: FaBullhorn },
    { id: 'general', label: 'General News', icon: FaTag },
    { id: 'job_posting', label: 'Job Opportunities', icon: FaBriefcase },
    { id: 'event', label: 'Events & Workshops', icon: FaCalendarAlt },
    { id: 'deadline', label: 'Important Deadlines', icon: FaClock }
  ];

  const priorityLevels = [
    { id: 'all', label: 'All Priorities' },
    { id: 'high', label: 'High Priority' },
    { id: 'medium', label: 'Medium Priority' },
    { id: 'low', label: 'Low Priority' }
  ];

  const sortOptions = [
    { id: 'newest', label: 'Newest First' },
    { id: 'oldest', label: 'Oldest First' },
    { id: 'priority', label: 'By Priority' },
    { id: 'relevance', label: 'Most Relevant' }
  ];

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Cache key generation for saved announcements
  const getSavedCacheKey = () => {
    return `savedAnnouncements_${user?.userd?.uid || 'anonymous'}`;
  };

  // Load saved status from cache
  const loadSavedAnnouncementsFromCache = useCallback(() => {
    try {
      const cacheKey = getSavedCacheKey();
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsedCache = JSON.parse(cached);
        
        // Clean old entries (older than 90 days for saved items)
        const ninetyDaysAgo = Date.now() - (90 * 24 * 60 * 60 * 1000);
        const cleanedCache = parsedCache.filter(item => 
          !item.timestamp || item.timestamp > ninetyDaysAgo
        );
        
        // Extract just the IDs
        const savedIds = cleanedCache.map(item => 
          typeof item === 'string' ? item : item.id
        );
        
        setSavedAnnouncements(savedIds);
        
        // Save cleaned cache back
        if (cleanedCache.length !== parsedCache.length) {
          localStorage.setItem(cacheKey, JSON.stringify(cleanedCache));
        }
      }
    } catch (error) {
      console.error('Error loading saved announcements from cache:', error);
      setSavedAnnouncements([]);
    }
  }, [user?.userd?.uid]);

  // Save announcement to cache
  const saveAnnouncementToCache = useCallback((announcementId) => {
    try {
      const cacheKey = getSavedCacheKey();
      const existing = localStorage.getItem(cacheKey);
      let savedList = [];
      
      if (existing) {
        savedList = JSON.parse(existing);
      }
      
      // Check if already exists (prevent duplicates)
      const alreadyExists = savedList.some(item => 
        (typeof item === 'string' ? item : item.id) === announcementId
      );
      
      if (!alreadyExists) {
        // Add with timestamp for potential cleanup
        savedList.push({
          id: announcementId,
          timestamp: Date.now()
        });
        
        localStorage.setItem(cacheKey, JSON.stringify(savedList));
      }
    } catch (error) {
      console.error('Error saving announcement to cache:', error);
    }
  }, [user?.userd?.uid]);

  // Remove saved announcement from cache
  const removeSavedAnnouncementFromCache = useCallback((announcementId) => {
    try {
      const cacheKey = getSavedCacheKey();
      const existing = localStorage.getItem(cacheKey);
      
      if (existing) {
        const savedList = JSON.parse(existing);
        const filteredList = savedList.filter(item => 
          (typeof item === 'string' ? item : item.id) !== announcementId
        );
        
        localStorage.setItem(cacheKey, JSON.stringify(filteredList));
      }
    } catch (error) {
      console.error('Error removing saved announcement from cache:', error);
    }
  }, [user?.userd?.uid]);

  // Cache key generation for read announcements
  const getReadCacheKey = () => {
    return `readAnnouncements_${user?.userd?.uid || 'anonymous'}`;
  };

  // Load read status from cache
  const loadReadAnnouncementsFromCache = useCallback(() => {
    try {
      const cacheKey = getReadCacheKey();
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsedCache = JSON.parse(cached);
        
        // Clean old entries (older than 30 days)
        const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
        const cleanedCache = parsedCache.filter(item => 
          !item.timestamp || item.timestamp > thirtyDaysAgo
        );
        
        // Extract just the IDs
        const readIds = cleanedCache.map(item => 
          typeof item === 'string' ? item : item.id
        );
        
        setReadAnnouncements(readIds);
        
        // Save cleaned cache back
        if (cleanedCache.length !== parsedCache.length) {
          localStorage.setItem(cacheKey, JSON.stringify(cleanedCache));
        }
      }
    } catch (error) {
      console.error('Error loading read announcements from cache:', error);
      setReadAnnouncements([]);
    }
  }, [user?.userd?.uid]);

  // Save read status to cache
  const saveReadAnnouncementToCache = useCallback((announcementId) => {
    try {
      const cacheKey = getReadCacheKey();
      const existing = localStorage.getItem(cacheKey);
      let readList = [];
      
      if (existing) {
        readList = JSON.parse(existing);
      }
      
      // Check if already exists (prevent duplicates)
      const alreadyExists = readList.some(item => 
        (typeof item === 'string' ? item : item.id) === announcementId
      );
      
      if (!alreadyExists) {
        // Add with timestamp for potential cleanup
        readList.push({
          id: announcementId,
          timestamp: Date.now()
        });
        
        localStorage.setItem(cacheKey, JSON.stringify(readList));
      }
    } catch (error) {
      console.error('Error saving read announcement to cache:', error);
    }
  }, [user?.userd?.uid]);

  // Load user data and announcements
  useEffect(() => {
    if (user?.userd?.uid) {
      loadUserData();
      loadAnnouncements();
    } else {
      // Clear cached data for anonymous users
      setReadAnnouncements([]);
      setSavedAnnouncements([]);
    }
  }, [user]);

  const loadUserData = async () => {
    try {
      if (user?.userd?.uid) {
        const userDoc = await getDoc(doc(db, 'users', user.userd.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserInterests(userData.interests || []);
          // Removed setSavedAnnouncements - using cache only now
          // Removed setReadAnnouncements - using cache only
          
          setUserProfile({
            skills: userData.skills || [],
            interests: userData.interests || [],
            graduationYear: userData.education?.graduationYear || '',
            country: userData.personalInfo?.country || '',
            location: userData.personalInfo?.location || userData.personalInfo?.city || '',
            gpa: userData.education?.gpa || ''
          });
        }
        
        // Load both read and saved status from cache instead
        loadReadAnnouncementsFromCache();
        loadSavedAnnouncementsFromCache();
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadAnnouncements = () => {
    try {
      const announcementsRef = collection(db, 'announcements');
      const q = query(
        announcementsRef,
        where('isPublished', '==', true),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const announcementData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt ? new Date(doc.data().createdAt) : new Date(),
          expiresAt: doc.data().expiresAt ? new Date(doc.data().expiresAt) : null
        }));

        // Filter out expired announcements
        const activeAnnouncements = announcementData.filter(announcement => 
          !announcement.expiresAt || announcement.expiresAt > new Date()
        );

        setAnnouncements(activeAnnouncements);
        setLoading(false);
      }, (error) => {
        console.error('Error loading announcements:', error);
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up announcements listener:', error);
      setLoading(false);
    }
  };

  const hasAnyTargeting = (targetAudience) => {
    return (
      (targetAudience.skills?.length > 0) ||
      (targetAudience.interests?.length > 0) ||
      (targetAudience.countries?.length > 0) ||
      (targetAudience.graduationYear && targetAudience.graduationYear !== 'any') ||
      (targetAudience.location && targetAudience.location !== 'any') ||
      (targetAudience.gpa && targetAudience.gpa !== 'any')
    );
  };

  const checkIfAnnouncementTargetsUser = (announcement, userProfile) => {
    const { targetAudience } = announcement;
    if (!targetAudience) return false;
    
    if (!hasAnyTargeting(targetAudience)) return false;
    
    // Check skills
    if (targetAudience.skills?.length > 0) {
      const hasMatchingSkill = targetAudience.skills.some(skill => 
        userProfile.skills.some(userSkill => 
          userSkill.toLowerCase().includes(skill.toLowerCase()) ||
          skill.toLowerCase().includes(userSkill.toLowerCase())
        )
      );
      if (!hasMatchingSkill) return false;
    }
    
    // Check interests
    if (targetAudience.interests?.length > 0) {
      const hasMatchingInterest = targetAudience.interests.some(interest => 
        userProfile.interests.some(userInterest => 
          userInterest.toLowerCase().includes(interest.toLowerCase()) ||
          interest.toLowerCase().includes(userInterest.toLowerCase())
        )
      );
      if (!hasMatchingInterest) return false;
    }
    
    // Check graduation year
    if (targetAudience.graduationYear && 
        targetAudience.graduationYear !== 'any' && 
        userProfile.graduationYear) {
      if (targetAudience.graduationYear !== userProfile.graduationYear) return false;
    }
    
    // Check countries
    if (targetAudience.countries?.length > 0 && userProfile.country) {
      const isCountryMatch = targetAudience.countries.some(country =>
        country.toLowerCase() === userProfile.country.toLowerCase()
      );
      if (!isCountryMatch) return false;
    }
    
    // Check location
    if (targetAudience.location && 
        targetAudience.location !== 'any' && 
        userProfile.location) {
      const announcementLocation = targetAudience.location.toLowerCase();
      const userLocation = userProfile.location.toLowerCase();
      const isLocationMatch = announcementLocation.includes(userLocation) || 
                            userLocation.includes(announcementLocation) ||
                            announcementLocation === 'remote' ||
                            userLocation === 'remote';
      if (!isLocationMatch) return false;
    }
    
    // Check GPA
    if (targetAudience.gpa && 
        targetAudience.gpa !== 'any' && 
        userProfile.gpa) {
      const requiredGPA = parseFloat(targetAudience.gpa);
      const userGPA = parseFloat(userProfile.gpa);
      if (!isNaN(requiredGPA) && !isNaN(userGPA) && userGPA < requiredGPA) return false;
    }
    
    return true;
  };

  const getRelevanceScore = (announcement) => {
    let score = 0;
    const { targetAudience } = announcement;
    
    if (!targetAudience || !userProfile) return 0;
    
    // Score based on matching criteria
    if (targetAudience.skills?.length > 0 && userProfile.skills?.length > 0) {
      const matchingSkills = targetAudience.skills.filter(skill => 
        userProfile.skills.some(userSkill => 
          userSkill.toLowerCase().includes(skill.toLowerCase()) ||
          skill.toLowerCase().includes(userSkill.toLowerCase())
        )
      ).length;
      score += matchingSkills * 5;
    }
    
    if (targetAudience.interests?.length > 0 && userProfile.interests?.length > 0) {
      const matchingInterests = targetAudience.interests.filter(interest => 
        userProfile.interests.some(userInterest => 
          userInterest.toLowerCase().includes(interest.toLowerCase()) ||
          interest.toLowerCase().includes(userInterest.toLowerCase())
        )
      ).length;
      score += matchingInterests * 3;
    }
    
    // Bonus for exact matches
    if (targetAudience.graduationYear === userProfile.graduationYear) score += 2;
    if (targetAudience.countries?.includes(userProfile.country)) score += 2;
    
    // Priority bonus
    const priorityBonus = { high: 3, medium: 2, low: 1 };
    score += priorityBonus[announcement.priority] || 0;
    
    // Recency bonus
    const daysSinceCreated = (new Date() - new Date(announcement.createdAt)) / (1000 * 60 * 60 * 24);
    if (daysSinceCreated < 7) score += 1;
    
    return score;
  };

  // Memoize filtered announcements
  const filteredAnnouncements = useMemo(() => {
    let filtered = [...announcements];

    // Apply targeting filter
    if (user?.userd?.uid && userProfile.skills.length > 0) {
      filtered = filtered.filter(announcement => {
        const { targetAudience } = announcement;
        
        if (showTargetedOnly) {
          return targetAudience && checkIfAnnouncementTargetsUser(announcement, userProfile);
        }
        
        if (!targetAudience || !hasAnyTargeting(targetAudience)) {
          return true;
        }
        
        return checkIfAnnouncementTargetsUser(announcement, userProfile);
      });
    }

    // Apply search filter
    if (debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(announcement =>
        announcement.title?.toLowerCase().includes(searchLower) ||
        announcement.content?.toLowerCase().includes(searchLower) ||
        announcement.employerName?.toLowerCase().includes(searchLower) ||
        announcement.type?.toLowerCase().includes(searchLower)
      );
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(announcement => announcement.type === selectedCategory);
    }

    // Apply priority filter
    if (selectedPriority !== 'all') {
      filtered = filtered.filter(announcement => announcement.priority === selectedPriority);
    }

    // Apply saved filter
    if (showSavedOnly) {
      filtered = filtered.filter(announcement => savedAnnouncements.includes(announcement.id));
    }

    // Sort announcements
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'priority':
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          const priorityDiff = (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
          if (priorityDiff !== 0) return priorityDiff;
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'relevance':
          return getRelevanceScore(b) - getRelevanceScore(a);
        default:
          return 0;
      }
    });

    return filtered;
  }, [
    announcements, 
    debouncedSearchTerm, 
    selectedCategory, 
    selectedPriority, 
    sortBy, 
    showSavedOnly, 
    showTargetedOnly,
    userProfile,
    savedAnnouncements,
    user?.userd?.uid
  ]);

  // Check if user has complete profile
  const hasCompleteProfile = useMemo(() => {
    return userProfile && (
      userProfile.skills.length > 0 || 
      userProfile.interests.length > 0 ||
      userProfile.graduationYear ||
      userProfile.country
    );
  }, [userProfile]);

  // Callback for save toggle (CACHE ONLY - NO FIREBASE WRITES)
  const handleToggleSave = useCallback((announcementId) => {
    const isSaved = savedAnnouncements.includes(announcementId);

    if (isSaved) {
      // Remove from saved
      setSavedAnnouncements(prev => prev.filter(id => id !== announcementId));
      removeSavedAnnouncementFromCache(announcementId);
    } else {
      // Add to saved
      setSavedAnnouncements(prev => [...prev, announcementId]);
      saveAnnouncementToCache(announcementId);
    }
  }, [savedAnnouncements, saveAnnouncementToCache, removeSavedAnnouncementFromCache]);

  // Callback for marking as read (CACHE ONLY - NO FIREBASE WRITES)
  const handleMarkAsRead = useCallback((announcementId) => {
    // Check if already read
    if (readAnnouncements.includes(announcementId)) return;

    // Update local state immediately for instant UI feedback
    setReadAnnouncements(prev => [...prev, announcementId]);
    
    // Save to cache (no Firebase write!)
    saveReadAnnouncementToCache(announcementId);
  }, [readAnnouncements, saveReadAnnouncementToCache]);

    // Handle viewing announcement in modal
  const handleViewAnnouncement = useCallback((announcement) => {
    setSelectedAnnouncement(announcement);
    setShowModal(true);
    handleMarkAsRead(announcement.id);
  }, [handleMarkAsRead]);

  // Close modal
  const closeModal = useCallback(() => {
    setShowModal(false);
    setSelectedAnnouncement(null);
  }, []);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getCategoryIcon = (type) => {
    switch (type) {
      case 'job_posting': return FaBriefcase;
      case 'event': return FaCalendarAlt;
      case 'deadline': return FaClock;
      default: return FaBullhorn;
    }
  };

  const getTargetingMatch = (announcement) => {
    const { targetAudience } = announcement;
    if (!targetAudience || !user?.userd?.uid || !userProfile) return null;
    
    const matches = [];
    let relevanceScore = 0;
    
    if (targetAudience.skills?.length > 0 && userProfile.skills?.length > 0) {
      const matchingSkills = targetAudience.skills.filter(skill => 
        userProfile.skills.some(userSkill => 
          userSkill.toLowerCase().includes(skill.toLowerCase()) ||
          skill.toLowerCase().includes(userSkill.toLowerCase())
        )
      );
      if (matchingSkills.length > 0) {
        matches.push(`Skills: ${matchingSkills.join(', ')}`);
        relevanceScore += matchingSkills.length * 3;
      }
    }
    
    if (targetAudience.interests?.length > 0 && userProfile.interests?.length > 0) {
      const matchingInterests = targetAudience.interests.filter(interest => 
        userProfile.interests.some(userInterest => 
          userInterest.toLowerCase().includes(interest.toLowerCase()) ||
          interest.toLowerCase().includes(userInterest.toLowerCase())
        )
      );
      if (matchingInterests.length > 0) {
        matches.push(`Interests: ${matchingInterests.join(', ')}`);
        relevanceScore += matchingInterests.length * 2;
      }
    }
    
    if (targetAudience.graduationYear === userProfile.graduationYear && 
        targetAudience.graduationYear !== 'any') {
      matches.push(`Graduation: Class of ${targetAudience.graduationYear}`);
      relevanceScore += 2;
    }
    
    if (targetAudience.countries?.includes(userProfile.country)) {
      matches.push(`Location: ${userProfile.country}`);
      relevanceScore += 1;
    }
    
    return { matches, relevanceScore };
  };

  // Render the announcement modal
  const renderAnnouncementModal = () => {
    if (!showModal || !selectedAnnouncement) return null;

    const CategoryIcon = getCategoryIcon(selectedAnnouncement.type);
    const targetingData = getTargetingMatch(selectedAnnouncement);
    const isTargeted = targetingData && targetingData.matches.length > 0;
    const isHighlyTargeted = targetingData && targetingData.relevanceScore >= 5;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <CategoryIcon className="text-orange-500 text-2xl" />
              <h2 className="text-2xl font-bold text-gray-900">
                {selectedAnnouncement.title}
              </h2>
            </div>
            <button
              onClick={closeModal}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          {/* Modal Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {/* Priority and Status Badges */}
            <div className="flex items-center flex-wrap gap-2 mb-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(selectedAnnouncement.priority)}`}>
                {selectedAnnouncement.priority?.toUpperCase()} PRIORITY
              </span>
              
              {isHighlyTargeted && (
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium flex items-center">
                  <FaBullhorn className="mr-1" />
                  HIGHLY TARGETED
                </span>
              )}
              
              {isTargeted && !isHighlyTargeted && (
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  TARGETED
                </span>
              )}
            </div>

            {/* Targeting Information */}
            {isTargeted && (
              <div className={`mb-6 p-4 rounded-lg border ${
                isHighlyTargeted 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-center mb-2">
                  <FaBullhorn className={`mr-2 ${
                    isHighlyTargeted ? 'text-green-600' : 'text-blue-600'
                  }`} />
                  <h3 className={`font-medium ${
                    isHighlyTargeted ? 'text-green-800' : 'text-blue-800'
                  }`}>
                    {isHighlyTargeted ? 'Highly relevant to you:' : 'Why you\'re seeing this:'}
                  </h3>
                </div>
                <div className={`${
                  isHighlyTargeted ? 'text-green-700' : 'text-blue-700'
                }`}>
                  {targetingData.matches.map((match, index) => (
                    <div key={index} className="flex items-center mb-1">
                      <span className="w-2 h-2 bg-current rounded-full mr-3"></span>
                      {match}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Main Content */}
            <div className="prose max-w-none">
              <div 
                className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                style={{ fontSize: '16px', lineHeight: '1.6' }}
              >
                {selectedAnnouncement.content}
              </div>
            </div>

            {/* Additional Details */}
            {selectedAnnouncement.additionalDetails && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Additional Information</h4>
                <div className="text-blue-800 whitespace-pre-wrap">
                  {selectedAnnouncement.additionalDetails}
                </div>
              </div>
            )}

            {/* Contact Information */}
            {(selectedAnnouncement.contactEmail || selectedAnnouncement.contactPhone) && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Contact Information</h4>
                <div className="space-y-1">
                  {selectedAnnouncement.contactEmail && (
                    <div className="text-gray-700">
                      <span className="font-medium">Email:</span> {selectedAnnouncement.contactEmail}
                    </div>
                  )}
                  {selectedAnnouncement.contactPhone && (
                    <div className="text-gray-700">
                      <span className="font-medium">Phone:</span> {selectedAnnouncement.contactPhone}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-6 flex flex-wrap gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleSave(selectedAnnouncement.id);
                }}
                className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                  savedAnnouncements.includes(selectedAnnouncement.id)
                    ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {savedAnnouncements.includes(selectedAnnouncement.id) ? <FaBookmark className="mr-2" /> : <FaRegBookmark className="mr-2" />}
                {savedAnnouncements.includes(selectedAnnouncement.id) ? 'Saved' : 'Save'}
              </button>

              {selectedAnnouncement.type === 'job_posting' && selectedAnnouncement.applicationUrl && (
                <a
                  href={selectedAnnouncement.applicationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors"
                >
                  <FaExternalLinkAlt className="mr-2" />
                  Apply Now
                </a>
              )}

              {selectedAnnouncement.websiteUrl && (
                <a
                  href={selectedAnnouncement.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md font-medium hover:bg-green-700 transition-colors"
                >
                  <FaGlobe className="mr-2" />
                  Visit Website
                </a>
              )}
            </div>

            {/* Announcement Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center text-gray-600">
                <FaBuilding className="mr-2" />
                <span className="font-medium">Company:</span>
                <span className="ml-1">{selectedAnnouncement.employerName}</span>
              </div>
              
              <div className="flex items-center text-gray-600">
                <FaCalendarAlt className="mr-2" />
                <span className="font-medium">Posted:</span>
                <span className="ml-1">{selectedAnnouncement.createdAt.toLocaleDateString()}</span>
              </div>
              
              {selectedAnnouncement.expiresAt && (
                <div className="flex items-center text-gray-600">
                  <FaClock className="mr-2" />
                  <span className="font-medium">Expires:</span>
                  <span className="ml-1">{selectedAnnouncement.expiresAt.toLocaleDateString()}</span>
                </div>
              )}
              
              <div className="flex items-center text-gray-600">
                <FaTag className="mr-2" />
                <span className="font-medium">Category:</span>
                <span className="ml-1 capitalize">{selectedAnnouncement.type?.replace('_', ' ')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAnnouncementCard = (announcement) => {
    const CategoryIcon = getCategoryIcon(announcement.type);
    const isRead = readAnnouncements.includes(announcement.id);
    const isSaved = savedAnnouncements.includes(announcement.id);
    
    const targetingData = getTargetingMatch(announcement);
    const isTargeted = targetingData && targetingData.matches.length > 0;
    const isHighlyTargeted = targetingData && targetingData.relevanceScore >= 5;

    return (
      <div
        key={announcement.id}
        className={`bg-white rounded-lg shadow-sm border transition-all duration-200 hover:shadow-md ${
          !isRead 
            ? 'border-l-4 border-l-orange-500 border-gray-200' 
            : 'border-gray-200'
        } ${
          isHighlyTargeted 
            ? 'ring-2 ring-green-200 bg-green-50' 
            : isTargeted 
            ? 'ring-1 ring-blue-200 bg-blue-50' 
            : ''
        } p-6`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center flex-wrap gap-2 mb-3">
              <CategoryIcon className="text-orange-500 text-lg" />
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(announcement.priority)}`}>
                {announcement.priority?.toUpperCase()}
              </span>
              {!isRead && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  NEW
                </span>
              )}
              {isHighlyTargeted && (
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center">
                  <FaBullhorn className="mr-1" />
                  HIGHLY TARGETED
                </span>
              )}
              {isTargeted && !isHighlyTargeted && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  TARGETED
                </span>
              )}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-2 hover:text-orange-600 transition-colors">
              {announcement.title}
            </h3>

            <p className="text-gray-700 mb-4 line-clamp-3">
              {announcement.content}
            </p>

            {/* Show targeting matches */}
            {isTargeted && (
              <div className={`mb-4 p-3 rounded-lg border ${
                isHighlyTargeted 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-center mb-1">
                  <FaBullhorn className={`mr-2 ${
                    isHighlyTargeted ? 'text-green-600' : 'text-blue-600'
                  }`} />
                  <h4 className={`text-sm font-medium ${
                    isHighlyTargeted ? 'text-green-800' : 'text-blue-800'
                  }`}>
                    {isHighlyTargeted ? 'Highly relevant to you:' : 'Why you\'re seeing this:'}
                  </h4>
                </div>
                <div className={`text-sm ${
                  isHighlyTargeted ? 'text-green-700' : 'text-blue-700'
                }`}>
                  {targetingData.matches.map((match, index) => (
                    <div key={index} className="flex items-center">
                      <span className="w-1 h-1 bg-current rounded-full mr-2"></span>
                      {match}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center flex-wrap gap-4 text-sm text-gray-500">
              <div className="flex items-center">
                <FaBuilding className="mr-1" />
                {announcement.employerName}
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="mr-1" />
                {announcement.createdAt.toLocaleDateString()}
              </div>
              {announcement.expiresAt && (
                <div className="flex items-center">
                  <FaClock className="mr-1" />
                  Expires: {announcement.expiresAt.toLocaleDateString()}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col items-center space-y-2 ml-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewAnnouncement(announcement);
              }}
              className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
              title="View full announcement"
            >
              <FaEye className="mr-2" />
              View
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleToggleSave(announcement.id);
              }}
              className={`p-2 rounded-full transition-colors ${
                isSaved 
                  ? 'text-orange-500 bg-orange-100 hover:bg-orange-200' 
                  : 'text-gray-400 hover:text-orange-500 hover:bg-gray-100'
              }`}
              title={isSaved ? 'Remove from saved' : 'Save announcement'}
            >
              {isSaved ? <FaBookmark /> : <FaRegBookmark />}
            </button>
            
            {announcement.type === 'job_posting' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-100 rounded-full transition-colors"
                title="View job details"
              >
                <FaExternalLinkAlt />
              </button>
            )}
          </div>
        </div>
        {/* Announcement Modal */}
        {renderAnnouncementModal()}
      </div>
    );
  };

  const renderFilters = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        {/* Search */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search announcements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>

        {/* Filter Toggles */}
        <div className="flex items-center space-x-4">
          {user?.userd?.uid && (
            <button
              onClick={() => setShowTargetedOnly(!showTargetedOnly)}
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                showTargetedOnly 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FaBullhorn className="mr-1" />
              Targeted Only
            </button>
          )}
          
          <button
            onClick={() => setShowSavedOnly(!showSavedOnly)}
            className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
              showSavedOnly 
                ? 'bg-orange-100 text-orange-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FaBookmark className="mr-1" />
            Saved Only
          </button>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium"
          >
            <FaFilter className="mr-1" />
            Filters
            <FaChevronDown className={`ml-1 transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Extended Filters */}
      {showFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {priorityLevels.map(level => (
                  <option key={level.id} value={level.id}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {sortOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading announcements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <FaBullhorn className="text-orange-500 text-3xl mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Announcements</h1>
          </div>
          <p className="text-gray-600">
            Stay updated with the latest opportunities, events, and important information
          </p>
          
          {/* Stats Summary */}
          {filteredAnnouncements.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-4 text-sm">
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                {filteredAnnouncements.length} announcement{filteredAnnouncements.length !== 1 ? 's' : ''}
              </span>
              {readAnnouncements.length > 0 && (
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full">
                  {readAnnouncements.length} read (cached)
                </span>
              )}
              {savedAnnouncements.length > 0 && (
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full">
                  {savedAnnouncements.length} saved (cached)
                </span>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        {renderFilters()}

        {/* Profile Completion Reminder */}
        {user?.userd?.uid && !hasCompleteProfile && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <FaInfoCircle className="text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800">Complete Your Profile</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Add your skills, interests, and other profile information to see more relevant announcements.
                </p>
                <Link 
                  to="/profile" 
                  className="inline-flex items-center mt-2 text-sm text-yellow-800 hover:text-yellow-900 underline"
                >
                  Update Profile <FaExternalLinkAlt className="ml-1" />
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Announcements List */}
        <div className="space-y-6">
          {filteredAnnouncements.length === 0 ? (
            <div className="text-center py-12">
              <FaBullhorn className="text-gray-400 text-5xl mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No announcements found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || selectedCategory !== 'all' || selectedPriority !== 'all' || showSavedOnly || showTargetedOnly
                  ? 'Try adjusting your filters to see more announcements.'
                  : 'Check back later for new announcements and opportunities.'}
              </p>
              
              {/* Quick action buttons */}
              <div className="flex justify-center space-x-4">
                {(searchTerm || selectedCategory !== 'all' || selectedPriority !== 'all' || showSavedOnly || showTargetedOnly) && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('all');
                      setSelectedPriority('all');
                      setShowSavedOnly(false);
                      setShowTargetedOnly(false);
                    }}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md font-medium transition-colors"
                  >
                    Clear All Filters
                  </button>
                )}
                
                {!hasCompleteProfile && (
                  <Link
                    to="/profile"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md font-medium transition-colors"
                  >
                    Complete Profile
                  </Link>
                )}
              </div>
            </div>
          ) : (
            filteredAnnouncements.map((announcement) => 
              renderAnnouncementCard(announcement)
            )
          )}
        </div>

        {/* Load More Button (if implementing pagination) */}
        {filteredAnnouncements.length > 0 && filteredAnnouncements.length >= 10 && (
          <div className="text-center mt-8">
            <button className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-md font-medium transition-colors">
              Load More Announcements
            </button>
          </div>
        )}

        {/* Floating Action Button for Quick Filters */}
        {user?.userd?.uid && (
          <div className="fixed bottom-6 right-6 z-10">
            <div className="flex flex-col space-y-2">
              {showTargetedOnly && (
                <button
                  onClick={() => setShowTargetedOnly(false)}
                  className="bg-green-500 hover:bg-green-600 text-white p-3 rounded-full shadow-lg transition-colors"
                  title="Show all announcements"
                >
                  <FaGlobe />
                </button>
              )}
              
              {!showTargetedOnly && hasCompleteProfile && (
                <button
                  onClick={() => setShowTargetedOnly(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-full shadow-lg transition-colors"
                  title="Show targeted only"
                >
                  <FaBullhorn />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Announcements;